// MediaDownloadFallback.jsx — download-first body for unpreviewable media

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatFileSize, getNodeFileSizeBytes } from 'utilities/Files';
import { isPdfMimeType } from 'utilities/fileMime';

// Services
import {
  downloadNodeMedia,
  getNodeMediaDownloadName,
  resolveNodeMediaDownloadUrl,
} from 'services/nodes/nodeMediaDownload';
// CSS
import './MediaDownloadFallback.css';

// Icons
import DocumentIcon from 'svg/DocumentIcon';
import Icon from 'svg/Icon';
import PdfIcon from 'svg/PdfIcon';
import PresentationIcon from 'svg/PresentationIcon';
import SpreadsheetIcon from 'svg/SpreadsheetIcon';

function resolveDocumentIcon(node) {
  const mimeType = String(
    node?.data_document?.mimeType
    || node?.data_drive?.mimeType
    || '',
  ).toLowerCase();
  const ext = String(
    node?.data_document?.extension
    || node?.data_document?.filename?.split('.')?.pop()
    || node?.fileName?.split('.')?.pop()
    || '',
  ).toLowerCase();

  if (ext === 'pdf' || isPdfMimeType(mimeType)) return PdfIcon;

  if (
    ['ppt', 'pptx', 'pps', 'ppsx', 'odp', 'key'].includes(ext)
    || mimeType.startsWith('application/vnd.ms-powerpoint')
    || mimeType.includes('presentation')
  ) {
    return PresentationIcon;
  }

  if (
    ['xls', 'xlsx', 'csv', 'tsv', 'ods', 'xlsm', 'numbers'].includes(ext)
    || mimeType.includes('spreadsheet')
    || mimeType.includes('excel')
  ) {
    return SpreadsheetIcon;
  }

  return DocumentIcon;
}

/**
 * MediaDownloadFallback
 *
 * Centered download affordance used when a media node has no useful in-app preview.
 * Host-pointer / Drive-linked docs without a local file get an open-in-Google CTA instead.
 *
 * @param {object} node
 * @param {string} [className]
 */
const MediaDownloadFallback = ({ node, className = '' }) => {
  const { theme } = useTheme();

  const [remoteUrl, setRemoteUrl] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [busy, setBusy] = useState(false);

  const isBlob = Boolean(
    (node?.url && String(node.url).startsWith('blob:'))
    || (node?.data_document?.url && String(node.data_document.url).startsWith('blob:'))
    || (node?.data_file?.url && String(node.data_file.url).startsWith('blob:')),
  );

  const fileName = useMemo(() => getNodeMediaDownloadName(node), [node]);
  const fileSizeBytes = useMemo(() => getNodeFileSizeBytes(node), [node]);
  const formattedSize = fileSizeBytes != null
    ? formatFileSize(fileSizeBytes, { decimals: 1 })
    : '';

  const IconComponent = useMemo(() => {
    const type = String(node?.type || '').toUpperCase();
    if (type === 'DOCUMENT' || type === 'FILE') return resolveDocumentIcon(node);
    return DocumentIcon;
  }, [node]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isBlob) {
        if (!cancelled) {
          setRemoteUrl(node?.url || node?.data_document?.url || node?.data_file?.url || null);
        }
        return;
      }
      const url = await resolveNodeMediaDownloadUrl(node);
      if (cancelled) return;
      setRemoteUrl(url || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [node, isBlob]);

  const handleDownloadClick = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!remoteUrl || busy) return;
    setBusy(true);
    try {
      await downloadNodeMedia(node);
    } finally {
      setBusy(false);
    }
  };

  if (!node) return null;

  const rootClass = [
    isBlob ? 'media-download-fallback media-download-fallback--local' : 'media-download-fallback',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      style={isBlob ? { backgroundColor: theme.panel } : undefined}
    >
      {isBlob ? (
        <div className="media-download-fallback-local">
          <IconComponent
            color={theme.foregroundMuted}
            height={80}
            width={80}
          />
          <div className="media-download-fallback-filename">
            {fileName}
          </div>
        </div>
      ) : remoteUrl ? (
        <div className="media-download-fallback-center">
          <div
            className="media-download-fallback-button"
            style={{
              background: hovered ? theme.backgroundHover : 'transparent',
              borderColor: hovered ? theme.backgroundHover : theme.backgroundMuted,
              boxShadow: hovered ? `0 0 30px ${theme.status}` : 'none',
              opacity: busy ? 0.7 : 1,
            }}
            aria-label={`Download ${fileName}`}
            role="button"
            tabIndex={0}
            onClick={handleDownloadClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleDownloadClick(event);
              }
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <Icon
              iconKey="download"
              width={80}
              height={80}
            />
            <div className="media-download-fallback-text">
              <div className="media-download-fallback-name">
                {fileName}
              </div>
              {formattedSize ? (
                <div className="media-download-fallback-size">
                  {formattedSize}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="media-download-fallback-placeholder">
          <h3>{node.title || 'File'}</h3>
          {fileName ? <p>File: {fileName}</p> : null}
          <p>No file URL available</p>
        </div>
      )}
    </div>
  );
};

MediaDownloadFallback.displayName = 'MediaDownloadFallback';

export default MediaDownloadFallback;
